import React from 'react';
import { ArrowRightIcon, MapPinIcon } from '@heroicons/react/24/outline';
import ScrollReveal from './ScrollReveal';
import { useLanguage } from '../LanguageContext';

const rolesEn = [
  {
    title: 'Producer',
    button: 'Join us as Producer',
    href: '/join/producer',
    points: ['Better income', 'We pay a better price for the milk', 'Timely payments', 'Clear collection plan'],
  },
  {
    title: 'Collector',
    button: 'Join us as Collector',
    href: '/join/collector',
    points: ['Better income', 'Incentives for quality and volume', 'Digital collection records', 'Fast, traceable payments'],
  },
  {
    title: 'Distributor',
    button: 'Join us as Distributor',
    href: '/join/distributor',
    points: ['Better income', 'Digital inventory records', 'Accurate settlements', 'Timely feedback', 'More income based on container volume delivered'],
  },
  {
    title: 'Logistics',
    button: 'Join us as Logistics',
    href: '/join/logistic',
    points: ['Better income', 'Reliable routes', 'Digital movement records', 'Clear settlements', 'Move milk and containers safely'],
  },
];

const rolesHi = [
  {
    title: 'उत्पादक',
    button: 'उत्पादक के रूप में जुड़ें',
    href: '/join/producer',
    points: ['बेहतर आय', 'दूध का बेहतर मूल्य', 'समय पर भुगतान', 'स्पष्ट संग्रह योजना'],
  },
  {
    title: 'संग्रहकर्ता',
    button: 'संग्रहकर्ता के रूप में जुड़ें',
    href: '/join/collector',
    points: ['बेहतर आय', 'गुणवत्ता और मात्रा के लिए प्रोत्साहन', 'डिजिटल संग्रह रिकॉर्ड', 'तेज़ और ट्रेस करने योग्य भुगतान'],
  },
  {
    title: 'वितरक',
    button: 'वितरक के रूप में जुड़ें',
    href: '/join/distributor',
    points: ['बेहतर आय', 'डिजिटल इन्वेंटरी रिकॉर्ड', 'सटीक सेटलमेंट', 'समय पर फीडबैक', 'डिलीवर किए गए कंटेनर वॉल्यूम के आधार पर अधिक आय'],
  },
  {
    title: 'लॉजिस्टिक्स',
    button: 'लॉजिस्टिक्स के रूप में जुड़ें',
    href: '/join/logistic',
    points: ['बेहतर आय', 'भरोसेमंद रूट', 'डिजिटल मूवमेंट रिकॉर्ड', 'स्पष्ट सेटलमेंट', 'दूध और कंटेनरों की सुरक्षित आवाजाही'],
  },
];

const JoinUsOverview: React.FC = () => {
  const { language } = useLanguage();
  const isHindi = language === 'hi';
  const roles = isHindi ? rolesHi : rolesEn;

  return (
  <section id="join-us" className="scroll-mt-24 bg-white py-16 md:py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
      <ScrollReveal>
        <div className="mx-auto max-w-3xl text-center">
          <p className="inline-flex items-center rounded-full bg-[rgba(27,94,32,0.08)] px-4 py-2 text-sm font-bold text-[var(--color-forest)]">
            <MapPinIcon className="mr-2 h-4 w-4" /> {isHindi ? 'जयपुर और आसपास के क्षेत्र' : 'Jaipur and nearby areas'}
          </p>
          <h2 className="mt-5 font-display text-4xl text-[var(--color-ink)] sm:text-5xl">{isHindi ? 'हमारे सफ़र से जुड़ें' : 'Join us in the journey'}</h2>
          <p className="mt-4 text-base leading-7 text-[var(--color-muted)]">
            {isHindi ? 'Rightoz के साथ बेहतर दूध नेटवर्क बनाएँ। चुनें कि आप हमारे साथ कैसे जुड़ना चाहते हैं।' : 'Build a better milk network with Rightoz — choose how you would like to join us'}
          </p>
        </div>
      </ScrollReveal>

      <div className="mt-10 grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {roles.map((role, index) => (
          <ScrollReveal key={role.title} delayMs={index * 80}>
            <article className="flex h-full flex-col rounded-[2rem] border border-[rgba(27,94,32,0.12)] bg-[var(--color-cream)] p-6 shadow-[0_16px_45px_rgba(27,94,32,0.08)] sm:p-7">
              <h3 className="font-display text-3xl text-[var(--color-forest)]">{role.title}</h3>
              <ul className="mt-5 flex-1 space-y-3 text-sm leading-6 text-[var(--color-muted)]">
                {role.points.map((point, pointIndex) => (
                  <li key={point} className={pointIndex === 0 ? 'font-extrabold text-[var(--color-forest)]' : 'flex gap-2'}>
                    {pointIndex > 0 && <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#7da66d]" />}
                    {point}
                  </li>
                ))}
              </ul>
              <a href={role.href} className="mt-7 inline-flex items-center justify-center rounded-full bg-[var(--color-forest)] px-5 py-3 text-sm font-bold text-white transition hover:-translate-y-0.5 hover:bg-[#174f1b]">
                {role.button}
                <ArrowRightIcon className="ml-2 h-4 w-4" />
              </a>
            </article>
          </ScrollReveal>
        ))}
      </div>
    </div>
  </section>
  );
};

export default JoinUsOverview;
